
import useSWR from "swr"
import {
  getMetrics,
  getInvestors,
  getInvestor,
  getActivities,
  getDealRooms,
  getAnalyses,
  getUpdates,
  getSearches,
  type ApiMetrics,
  type ApiInvestor,
  type ApiInvestorNote,
  type ApiActivity,
  type ApiDealRoom,
  type ApiAnalysis,
  type ApiUpdate,
  type ApiSearch,
} from "@/lib/api-client"
import {
  getCanvaConnectionStatus,
  getCanvaDesigns,
  type CanvaDesignItem,
} from "@/lib/canva-client"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasToken() {
  if (typeof document === "undefined") return false
  return document.cookie.includes("irene_token=")
}

// Shared SWR options: retry 3 times with exponential backoff, don't refetch on
// window focus for a quieter UX, and swallow errors on retries.
const sharedOpts = {
  revalidateOnFocus: false,
  errorRetryCount: 3,
  shouldRetryOnError: true,
  dedupingInterval: 5000,
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export function useMetrics() {
  return useSWR<ApiMetrics>(
    hasToken() ? "metrics" : null,
    () => getMetrics(),
    sharedOpts
  )
}

// ---------------------------------------------------------------------------
// Investors
// ---------------------------------------------------------------------------

export function useInvestors() {
  return useSWR<ApiInvestor[]>(
    hasToken() ? "investors" : null,
    () => getInvestors(),
    sharedOpts
  )
}

export function useInvestor(id: string | null) {
  return useSWR<ApiInvestor & { notes: ApiInvestorNote[] }>(
    id && hasToken() ? `investor-${id}` : null,
    () => getInvestor(id!),
    sharedOpts
  )
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

export function useActivities() {
  return useSWR<ApiActivity[]>(
    hasToken() ? "activities" : null,
    () => getActivities(),
    sharedOpts
  )
}

// ---------------------------------------------------------------------------
// Deal Rooms
// ---------------------------------------------------------------------------

export function useDealRooms() {
  return useSWR<ApiDealRoom[]>(
    hasToken() ? "deal-rooms" : null,
    () => getDealRooms(),
    sharedOpts
  )
}

// ---------------------------------------------------------------------------
// Analyses
// ---------------------------------------------------------------------------

export function useAnalyses() {
  return useSWR<ApiAnalysis[]>(
    hasToken() ? "analyses" : null,
    () => getAnalyses(),
    sharedOpts
  )
}

// ---------------------------------------------------------------------------
// Updates
// ---------------------------------------------------------------------------

export function useUpdates() {
  return useSWR<ApiUpdate[]>(
    hasToken() ? "updates" : null,
    () => getUpdates(),
    sharedOpts
  )
}

// ---------------------------------------------------------------------------
// Searches
// ---------------------------------------------------------------------------

export function useSearches() {
  return useSWR<ApiSearch[]>(
    hasToken() ? "searches" : null,
    () => getSearches(),
    sharedOpts
  )
}

// ---------------------------------------------------------------------------
// Canva
// ---------------------------------------------------------------------------

export function useCanvaConnection() {
  return useSWR<{ connected: boolean }>(
    hasToken() ? "canva-connection" : null,
    () => getCanvaConnectionStatus(),
    sharedOpts
  )
}

export function useCanvaDesigns() {
  return useSWR<CanvaDesignItem[]>(
    hasToken() ? "canva-designs" : null,
    () => getCanvaDesigns(),
    sharedOpts
  )
}
